import { useState, useEffect } from "react";
import { T } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Card, Btn, SectionTitle } from "../ui/index.jsx";

export default function DividendCalendar({ token, onBack }) {
  const [holdings, setHoldings] = useState([]);
  const [reports, setReports] = useState({});

  useEffect(() => {
    api("/holdings", {}, token).then(d => setHoldings(d || [])).catch(() => {});
    api("/reports?limit=10", {}, token).then(d => {
      const map = {};
      (Array.isArray(d) ? d : []).forEach(r => { if (r.symbol && !map[r.symbol]) map[r.symbol] = r; });
      setReports(map);
    }).catch(() => {});
  }, []);

  // 배당 정보 추출
  const dividendData = holdings.map(h => {
    const r = reports[h.symbol];
    const m = r?.micro || {};
    const divYield = m.dividend_yield || 0;
    const currentPrice = m.close || h.avg_price || 0;
    const qty = h.qty || 0;

    // 예상 배당금 = 현재가 × 배당수익률 × 보유수량
    const estDividend = Math.round(currentPrice * (divYield / 100) * qty);
    // 주당 배당금 추정
    const divPerShare = Math.round(currentPrice * (divYield / 100));

    return {
      symbol: h.symbol,
      name: h.name,
      qty,
      currentPrice,
      dividend_yield: divYield,
      div_per_share: divPerShare,
      est_total: estDividend,
      sector: m.sector_name,
    };
  }).filter(d => d.dividend_yield > 0);

  const totalEstDividend = dividendData.reduce((s, d) => s + d.est_total, 0);
  const avgYield = dividendData.length > 0 ? (dividendData.reduce((s, d) => s + d.dividend_yield, 0) / dividendData.length) : 0;

  // 한국 주식 일반 배당 시기 (12월 결산 → 3~4월 지급)
  const now = new Date();
  const currentMonth = now.getMonth() + 1;

  return (
    <div>
      <Btn variant="ghost" onClick={onBack}>← 돌아가기</Btn>

      <h2 style={{ fontSize: 24, fontWeight: 900, margin: "24px 0 8px" }}>💰 배당 캘린더</h2>
      <p style={{ fontSize: 13, color: T.textDim, marginBottom: 20 }}>보유 종목의 배당 정보와 예상 배당금을 확인하세요</p>

      {/* 요약 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
        <Card style={{ textAlign: "center", padding: 16 }}>
          <div style={{ fontSize: 13, color: T.textSub, fontWeight: 600 }}>배당 종목 수</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: T.accent }}>{dividendData.length}개</div>
          <div style={{ fontSize: 12, color: T.textSub }}>전체 {holdings.length}개 중</div>
        </Card>
        <Card style={{ textAlign: "center", padding: 16 }}>
          <div style={{ fontSize: 13, color: T.textSub, fontWeight: 600 }}>예상 연간 배당금</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: T.green }}>{totalEstDividend.toLocaleString()}원</div>
          <div style={{ fontSize: 12, color: T.textSub }}>매월 약 {Math.round(totalEstDividend / 12).toLocaleString()}원</div>
        </Card>
        <Card style={{ textAlign: "center", padding: 16 }}>
          <div style={{ fontSize: 13, color: T.textSub, fontWeight: 600 }}>평균 배당수익률</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: avgYield > 3 ? T.green : T.text }}>{avgYield.toFixed(2)}%</div>
          <div style={{ fontSize: 12, color: T.textSub }}>{avgYield > 3 ? "매력적 수준" : avgYield > 1 ? "보통 수준" : "낮은 수준"}</div>
        </Card>
      </div>

      {/* 배당 시즌 안내 */}
      <Card style={{ marginBottom: 16, borderLeft: `3px solid ${T.accent}` }}>
        <SectionTitle tip="대부분의 한국 상장사는 12월 결산 후 3~4월에 배당금을 지급해요">배당 시즌 안내</SectionTitle>
        <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
          {["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"].map((m, i) => {
            const isDividendSeason = [2, 3].includes(i); // 3~4월
            const isRecordDate = i === 11; // 12월
            const isCurrent = i === currentMonth - 1;
            return (
              <div key={i} style={{
                flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 6, fontSize: 12, fontWeight: 600,
                background: isCurrent ? T.accent + "30" : isDividendSeason ? T.green + "15" : isRecordDate ? T.yellow + "15" : T.bg,
                color: isCurrent ? T.accent : isDividendSeason ? T.green : isRecordDate ? T.yellow : T.textMuted,
                border: isCurrent ? `2px solid ${T.accent}` : "none",
              }}>{m.slice(0, 2)}</div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 14, fontSize: 12, color: T.textMuted }}>
          <span><span style={{ color: T.yellow }}>■</span> 배당 기준일 (12월)</span>
          <span><span style={{ color: T.green }}>■</span> 배당 지급 시기 (3~4월)</span>
          <span><span style={{ color: T.accent }}>■</span> 현재</span>
        </div>
      </Card>

      {/* 종목별 배당 정보 */}
      <Card>
        <SectionTitle tip="보유 종목별 예상 배당금이에요. 실제 배당은 회사 공시에 따라 달라질 수 있어요">종목별 배당 상세</SectionTitle>
        {dividendData.length === 0 ? (
          <div style={{ textAlign: "center", padding: 30, color: T.textMuted }}>
            배당 정보가 있는 종목이 없어요.<br />배당수익률이 있는 종목을 등록하면 자동으로 표시돼요.
          </div>
        ) : (
          dividendData.map((d, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: i < dividendData.length - 1 ? `1px solid ${T.border}` : "none" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{d.name}</div>
                <div style={{ fontSize: 11, color: T.textMuted }}>{d.symbol} · {d.qty}주 보유 · {d.sector || ""}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.green }}>
                  {d.est_total.toLocaleString()}원
                </div>
                <div style={{ fontSize: 11, color: T.textMuted }}>
                  주당 {d.div_per_share.toLocaleString()}원 · {d.dividend_yield.toFixed(2)}%
                </div>
              </div>
            </div>
          ))
        )}
      </Card>

      {/* 안내 */}
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 12, padding: "0 4px" }}>
        ※ 예상 배당금은 현재 주가와 최근 배당수익률을 기반으로 추정한 값이에요. 실제 배당금은 회사의 이사회 결정에 따라 달라질 수 있어요.
      </div>
    </div>
  );
}
